/**
 * WEB-927: эффективная раскладка экрана — ОДНА точка расчёта на все поверхности.
 *
 * Рендер квиза (QuizScreenWizard), рендер пейволла (PaywallScreenWizard),
 * превью конструктора (PhonePreview) и будущий редактор обязаны считать
 * одинаково: иначе «в превью одно, в проде другое» — тот же класс расхождения,
 * что уже ловили на хроме стиль-паков.
 *
 * Приоритет: явное значение ЭКРАНА > дефолт СТИЛЬ-ПАКА > легаси-дефолт.
 *
 * Асимметрия вуали (важно, не упрощать):
 *   • режим 'card' (в том числе отсутствие поля mode) — дефолт вуали ЛЕГАСИ
 *     'scrim-bottom'. Карточный фон рисует уже выкаченный пейволл
 *     (ScreenBackgroundWrapper), и его вид меняться не имеет права. Дефолт
 *     пака в card НЕ применяется, даже если пак объявил 'none'.
 *   • режим 'full-screen' — новый, ничего выкаченного не ломает, поэтому здесь
 *     дефолт берётся из пака (StylePack.layout.backgroundOverlay).
 *
 * Оси (alignY/alignX) резолвятся уже сейчас, но РЕНДЕР их — следующий атом:
 * поле готово, чтобы оси легли в существующий резолвер, а не в новый.
 */
import type { EditorBlock, EditorScreen, ScreenBackground } from '@/store/editorSlice';
import { screenHasFlexibleSpacer } from './flexSpacer';
import { getStylePackById } from '@/themes/stylePacks';
import { hexLuminance, parseCssColorToRgb } from '@/themes/colorUtils';
import { OPAQUE_ENOUGH_ALPHA } from '@/themes/surfaceOpacity';
import type { ScreenAlignX, ScreenAlignY, ScreenOverlayTone } from '@/themes/stylePack.types';

export type ScreenBackgroundMode = 'card' | 'full-screen';

export interface ResolvedScreenLayout {
  /** Сколько места занимает фоновая картинка. Отсутствие поля = 'card'. */
  mode: ScreenBackgroundMode;
  /** Эффективный тон вуали поверх картинки. */
  overlay: ScreenOverlayTone;
  /**
   * true — тон задан САМИМ экраном (а не унаследован от пака/легаси).
   * Рендерер по этому флагу решает, перебивать ли переменные пака
   * (--screen-overlay-direction/--screen-overlay-opacity) своими значениями.
   */
  overlayIsExplicit: boolean;
  /** Вертикальная ось контента (рендер — следующий атом). */
  alignY: ScreenAlignY;
  /** Горизонтальная ось контента (рендер — следующий атом). */
  alignX: ScreenAlignX;
  /**
   * На экране есть гибкий отступ (блок «Отступ» в режиме «Заполнить
   * свободное место»): экран забирает всю высоту страницы, а свободное место
   * распределяет отступ, не ось (см. flexSpacer.tsx).
   */
  fill: boolean;
  /** Есть ли что рисовать: режим сам по себе фон не включает. */
  hasBackgroundImage: boolean;
  /**
   * WEB-1805: css-значение СОБСТВЕННОЙ заливки экрана (цвет и/или градиент).
   * undefined — экран заливки не задавал и красится фоном воронки, как было
   * до карточки. Считается здесь, а не в рендерерах: их три (публичный квиз,
   * пейволл, превью конструктора), и копия правила разъехалась бы.
   */
  backgroundFill?: string;
  /**
   * WEB-1805: заливка тёмная — рендер обязан прижать светлый текст, иначе
   * тёмный текст темы на тёмном экране читается как пустота. Тот же приём,
   * что у вуали над фото (WEB-872), и тот же порог, что у цвета текста на
   * кнопке (`pickOnPrimaryColor`).
   */
  fillIsDark: boolean;
}

/**
 * Порог «поверхность тёмная». Значение общее с `pickOnPrimaryColor`
 * (themes/colorUtils.ts) и его бэковым зеркалом `readableTextOn` — три места
 * не имеют права разойтись в том, что считать тёмным.
 */
const DARK_FILL_LUMINANCE_THRESHOLD = 0.179;

/**
 * Ниже этой прозрачности заливка тёмной НЕ считается: под полупрозрачным
 * цветом просвечивает фон воронки, и «чёрный на 15%» поверх белой страницы —
 * светлая поверхность. Порог грубый намеренно: точный ответ требует знать
 * подложку, а её на этом слое нет.
 *
 * WEB-1859: значение переехало в `themes/surfaceOpacity.ts` и стало ОБЩИМ —
 * тот же вопрос решают ещё пять помощников (пилюля, плашка ошибки оплаты,
 * строка варианта, кнопка со значком, поверхность элемента), и у них были свои
 * ответы. Здесь оно не изменилось ни на сотую: 0.9 — единственный из прежних
 * трёх порогов, у которого было записано обоснование.
 */

/**
 * WEB-1805, ревизия (Б1): меряем ЛЮБУЮ форму записи цвета, которую принимает
 * схема (`black`, `rgb()/rgba()`, `hsl()`, `#rrggbbaa`), а не только код
 * цвета. Раньше `black` считался светлым, и «сделай третий экран тёмным»
 * давало тёмный экран с тёмным текстом.
 *
 * Неизмеримое (`var(--…)`, незнакомое имя) — НЕ тёмное: за переменной может
 * стоять что угодно, и перекрашивать текст наугад хуже, чем не трогать.
 */
function isDarkColor(color: string | undefined): boolean {
  if (!color) return false;
  const parsed = parseCssColorToRgb(color);
  if (!parsed || parsed.alpha < OPAQUE_ENOUGH_ALPHA) return false;
  return hexLuminance(color) <= DARK_FILL_LUMINANCE_THRESHOLD;
}

/**
 * Заливка экрана как css-значение свойства `background`.
 *
 * Градиент выигрывает у цвета, но цвет остаётся ПОСЛЕДНИМ слоем: градиент
 * бывает полупрозрачным, и без подложки под ним просвечивал бы фон воронки.
 */
function resolveBackgroundFill(
  background: ScreenBackground | undefined,
): string | undefined {
  const gradient = background?.gradient;
  const color = background?.color;
  if (gradient) {
    const layer = `linear-gradient(${gradient.angle}deg, ${gradient.from}, ${gradient.to})`;
    return color ? `${layer}, ${color}` : layer;
  }
  return color || undefined;
}

/**
 * Тёмная ли заливка. У градиента тёмным считается только тот, у кого тёмны ОБА
 * конца — иначе светлый текст пропал бы на светлом конце.
 *
 * WEB-1805, ревизия (М15) — два сознательных упрощения, чтобы их не «чинили»
 * вслепую при следующей правке:
 *  1. Мерка «оба конца», а не среднее. Среднее назвало бы тёмным градиент
 *     чёрный→средне-серый, и белый текст сел бы на светлую половину экрана с
 *     контрастом около 3:1. Порог по обоим концам строже и никогда не врёт в
 *     опасную сторону: он может НЕ прижать светлый текст там, где тот был бы
 *     уместен (текст останется темой — читаемо), но не прижмёт его туда, где
 *     он пропадёт.
 *  2. При наличии градиента `color` в замере НЕ участвует. Цвет под градиентом
 *     — подложка на случай полупрозрачных концов; видимая поверхность в этом
 *     случае смешанная, и посчитать её честно можно только зная альфу обоих
 *     концов. Пока такой замер не нужен, «есть градиент — меряем градиент».
 */
function resolveFillIsDark(
  background: ScreenBackground | undefined,
): boolean {
  const gradient = background?.gradient;
  if (gradient) return isDarkColor(gradient.from) && isDarkColor(gradient.to);
  return isDarkColor(background?.color);
}

/** Легаси-дефолты — «как рисует прод сегодня», последний рубеж приоритета. */
const LEGACY_OVERLAY: ScreenOverlayTone = 'scrim-bottom';
const LEGACY_ALIGN_Y: ScreenAlignY = 'top';
const LEGACY_ALIGN_X: ScreenAlignX = 'center';

export function resolveScreenLayout(
  screen:
    | (Pick<EditorScreen, 'background' | 'layout'> & {
        blocks?: readonly EditorBlock[];
      })
    | undefined,
  stylePackId: string | undefined,
): ResolvedScreenLayout {
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  const background = screen?.background;

  const mode: ScreenBackgroundMode = background?.mode === 'full-screen' ? 'full-screen' : 'card';

  const explicitOverlay = background?.overlay;
  const packOverlay = pack?.layout.backgroundOverlay;
  const overlay: ScreenOverlayTone =
    explicitOverlay ?? (mode === 'full-screen' ? (packOverlay ?? LEGACY_OVERLAY) : LEGACY_OVERLAY);

  return {
    mode,
    overlay,
    overlayIsExplicit: explicitOverlay !== undefined,
    alignY: screen?.layout?.alignY ?? pack?.layout.alignY ?? LEGACY_ALIGN_Y,
    alignX: screen?.layout?.alignX ?? pack?.layout.alignX ?? LEGACY_ALIGN_X,
    fill: screenHasFlexibleSpacer(screen?.blocks),
    hasBackgroundImage: typeof background?.imageUrl === 'string' && background.imageUrl.length > 0,
    backgroundFill: resolveBackgroundFill(background),
    fillIsDark: resolveFillIsDark(background),
  };
}
